import { useState } from "react";
import { Button, useNotify } from "react-admin";
import { useWatch } from "react-hook-form";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

import { studentRequests } from "api/studentApi";
import permissions from "app/permissions";
import dataProvider from "server/dataProvider";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import SecuredComponent from "server/security/SecuredComponent";
import { toDateOnly } from "utils/helpers";
import { useNotifyError } from "utils/useNotifyError";

const fullName = (first, second, last) => [first, second, last].filter(Boolean).join(" ");

// "Провери студент" — sits next to Запази and is run once the form is filled in: looks for an
// already enrolled student education matching ALL of the entered criteria exactly, so a foreigner
// is not enrolled twice.
const CheckStudentDialog = ({ disabled }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const [matches, setMatches] = useState([]);

  const specialityId = useWatch({ name: "specialityId" });
  const firstNameEn = useWatch({ name: "userInfo.firstNameEn" });
  const secondNameEn = useWatch({ name: "userInfo.secondNameEn" });
  const lastNameEn = useWatch({ name: "userInfo.lastNameEn" });
  const personId = useWatch({ name: "userInfo.personId" });
  const birthCountryId = useWatch({ name: "birthCountryId" });
  const birthDate = useWatch({ name: "birthDate" });

  // onOpen is NOT wrapped by the modal, so it handles its own errors and returns false to stay closed.
  const onOpen = async () => {
    if (!specialityId || !firstNameEn || !lastNameEn || !personId || !birthCountryId || !birthDate) {
      notify(
        "Попълнете специалност, име и фамилия на латиница, ЕГН/ЛНЧ/ПИД, държава и дата на раждане преди проверката!",
        { type: "error" },
      );
      return false;
    }

    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationExactMatch({
          specialityId,
          firstNameEn,
          secondNameEn,
          lastNameEn,
          personId,
          birthCountryId,
          dateOfBirth: toDateOnly(birthDate),
        }),
        null,
        "GET",
      );

      const found = Array.isArray(data) ? data : data ? [data] : [];

      // The expected case — nothing found, so there is nothing to show in a dialog.
      if (found.length === 0) {
        notify("Няма намерен студент с въведените данни.", { type: "success" });
        return false;
      }

      setMatches(found);
      return true;
    } catch (error) {
      notifyError(error);
      return false;
    }
  };

  return (
    <SecuredComponent permission={permissions.canPreviewStudentEducationInfo}>
      <ConfirmModal
        titleContent="Намерен студент"
        dialogSize="md"
        isConfirmButton={false}
        closeTitle="Затвори"
        onOpen={onOpen}
        resetProps={() => setMatches([])}
        triggerButton={
          <Button
            label="Провери студент"
            variant="contained"
            size="medium"
            disabled={disabled}
            startIcon={<PersonSearchIcon />}
          />
        }
      >
        <>
          <Typography
            color="error"
            fontWeight="bold"
            sx={{ padding: "1rem 0" }}
          >
            {matches.length === 1
              ? "Вече съществува студент с въведените данни:"
              : `Вече съществуват ${matches.length} студенти с въведените данни:`}
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 3, maxHeight: 400, overflowY: "auto" }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme => theme.palette.secondary.main }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Факултетен номер</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Имена на кирилица</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Имена на латиница</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matches.map((student, index) => (
                  <TableRow key={student.studentEducationId ?? index}>
                    <TableCell>{student.facultyNumber}</TableCell>
                    <TableCell>{fullName(student.firstName, student.secondName, student.lastName)}</TableCell>
                    <TableCell>{fullName(student.firstNameEn, student.secondNameEn, student.lastNameEn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      </ConfirmModal>
    </SecuredComponent>
  );
};

export default CheckStudentDialog;
